import { getApiUrl } from "../../../utils/baseUrl";
import styled from "styled-components";
import DOMPurify from "dompurify";
import type { WorkspaceRow, WorkspaceColumn, TradingPartner, EnrichmentCompany } from "./types";

type TableProps = {
  hideBorder?: boolean;
};

export const CustomStylesWorkspaceTable = styled.div<TableProps>`
  margin-bottom: 0px !important;
  font-family: "Inter", sans-serif;
  .new-trademo-table-container {
    width: 100%;
    min-height: 420px;
    overflow-x: auto;
    overflow-y: visible;
    border-top: 1px solid #eaecf0;
    border-bottom: none;
    background: #fff;
    ::-webkit-scrollbar {
      height: 6px;
    }
  }
  table {
    table-layout: fixed;
    border-spacing: 0;
    border-collapse: separate;
    font-weight: 500;
    .datatable-checkbox {
      font-size: 18px;
      cursor: pointer;
      padding: 0px;
      margin-top: -1px;
      vertical-align: middle;
      position: relative;
      :after {
        left: -1.05px;
      }
    }
    thead {
      font-size: 0.875rem !important;
      line-height: 1.25rem;
      border: 0;
      border-top: none;
      background-color: #fff;
      color: #101828;
      border-bottom: 1px solid #eaecf0;
      th {
        border-right: ${({ hideBorder }) => (hideBorder ? "none" : " 1px solid #eaecf0")};
        input {
          margin-bottom: 4px;
        }
        span {
          margin: auto 0px;
        }
        font-weight: 600;
        box-sizing: border-box;
        height: 42px;
        vertical-align: middle !important;
        border-top: none;
        .form-check {
          padding-left: 5px !important;
          position: relative;
          .form-check-input {
            position: absolute;
            margin-left: 0 !important;
            margin-top: 5px !important;
          }
        }
        &:last-child {
          border-right: none;
        }
      }
    }
    tbody {
      tr {
        td {
          min-height: 52px;
          height: auto;
          vertical-align: middle;
          color: #101828 !important;
          font-weight: 400 !important;
          border-right: 1px solid #eaecf0;
          border-right: ${({ hideBorder }) => (hideBorder ? "none" : " 1px solid #eaecf0")};
          &:last-child {
            border-right: none;
            background-color: #ffffff;
          }
        }
        svg {
          width: 18px;
          height: 18px;
          stroke: #667085;
          stroke-width: 2;
        }
        :hover {
          background-color: #f9fafb !important;
        }
        :hover td:nth-last-child(-n + 3) {
          background: #f9fafb !important;
        }
        &:last-child td {
          border-bottom: 1px solid #eaecf0 !important;
        }
      }
    }
    tr {
      padding: 15px;
      font-size: 0.875rem !important;
      line-height: 1.25rem;
    }
    th,
    td {
      margin: 0;
      padding: 0.625rem 0.75rem;
      border: ${({ hideBorder }) => (hideBorder ? "none" : " 1px solid #eaecf0")};
      border-bottom: ${({ hideBorder }) => (hideBorder ? " 1px solid #eaecf0" : "none")};
      border-left-style: none;
    }
  }
  .trademo-table-pagination-container {
    border-top: none;
  }
  .pagination {
    padding: 0.5rem;
  }
`;

export const workspaceApiEndpoints = {
  getIntelWorkspaces: getApiUrl("intelWorkspaces"),
  createIntelWorkspace: getApiUrl("intelWorkspaces"),
  deleteIntelWorkspace: (workspaceId: string | number) => getApiUrl(`intelWorkspace/${workspaceId}`),
  bulkDeleteWorkspaceCompanies: (workspaceId: string | number) => getApiUrl(`intelWorkspace/companies/bulk_delete/${workspaceId}`),
  getIntelWorkspaceColumns: (workspaceId: string | number) => getApiUrl(`intelWorkspace/columns/${workspaceId}`),
  updateIntelWorkspaceColumns: (workspaceId: string | number) => getApiUrl(`intelWorkspace/columns/${workspaceId}`),
  addIntelWorkspaceCompanies: (workspaceId: string | number) => getApiUrl(`intelWorkspace/companies/${workspaceId}`),
  getIntelWorkspaceCompanies: (workspaceId: string | number) => getApiUrl(`intelWorkspace/companies/${workspaceId}`),
  getIntelWorkspaceTradingPartners: getApiUrl("intelWorkspace/tradingPartners"),
  downloadIntelWorkspaceCompanies: (workspaceId: string | number) => getApiUrl(`intelWorkspace/download/${workspaceId}`),
  postEnrichmentCompanies: getApiUrl("enrichment/companies/"),
  pricingAgent: getApiUrl("enrichment/pricing-agent/"),
  aiSummary: getApiUrl("enrichment/ai-summary/"),
  sanctionsScreening: getApiUrl("enrichment/sanctions-screening/"),
  traderManufacturer: getApiUrl("enrichment/trader-manufacturer/"),
  enrichmentSources: getApiUrl("enrichment/sources/"),
  lookupIntelWorkspaceCompanies: getApiUrl("intelWorkspace/companies/lookup"),
};

export const getErrorMessage = (error: { response?: { data?: { message?: string } } } | unknown, fallbackMessage: string): string => (error as { response?: { data?: { message?: string } } })?.response?.data?.message || fallbackMessage;

export enum WorkspaceColumnKey {
  CompanyName = "company_name",
  CompanyDescription = "company_description",
  CompanyIndustry = "company_industry",
  CompanyDomain = "company_domain",
  CompanyCompetitors = "company_competitors",
  CompanyRevenueRange = "company_revenue_range",
  CompanyRevenueDisplay = "company_revenue_display",
  EmployeeCountRange = "employee_count_range",
  PricingAgent = "pricing_agent",
  TraderManufacturer = "trader_manufacturer",
  SanctionsScreening = "sanctions_screening",
  AiSummary = "ai_summary",
  TradingPartners = "trading_partners",
  TradingCountries = "trading_countries",
  EnrichedContact = "enriched_contact",
  Shipments = "shipments",
  Weight = "weight",
  ShipmentValue = "shipment_value",
  FirstImportedOn = "first_imported_on",
  LastImportedOn = "last_imported_on",
}

export const enrichmentColumnTooltips: Partial<Record<WorkspaceColumnKey, string>> = {
  [WorkspaceColumnKey.CompanyIndustry]: "This is an AI-enriched attribute. Enrichment is performed using fine-tuned LLMs that reference various open sources to determine a company's industry based on SIC and NAICS classifications. Note: In some cases, data may not be available at the source level.",
  [WorkspaceColumnKey.EnrichedContact]: "This is an AI-enriched attribute. Enrichment is performed using fine-tuned LLMs that reference our proprietary contact datasets to pull out contacts basis your preference. Note: In some cases, data may not be available at the source level.",
  [WorkspaceColumnKey.PricingAgent]: "This is an AI-enriched attribute. Enrichment is performed using fine-tuned LLMs on our global trade data to determine per unit pricing for the commodity. Note: Data is available for last 2 years only.",
  [WorkspaceColumnKey.SanctionsScreening]: "Screens the company against all active sanctions lists and CHPL at a 75% match threshold. Run it explicitly from the Enrich menu; results are stored and shown on your next visit until you re-screen.",
  [WorkspaceColumnKey.CompanyDescription]: "This is an AI-enriched attribute. Enrichment is performed using fine-tuned LLMs that reference various open sources to collate short summary about the company operations, background, industry and more. Note: In some cases, data may not be available at the source level.",
  [WorkspaceColumnKey.EmployeeCountRange]: "This is an AI-enriched attribute. Enrichment is performed using fine-tuned LLMs that reference various open sources to determine latest headcount of the company. Note: In some cases, data may not be available at the source level.",
  [WorkspaceColumnKey.CompanyRevenueDisplay]: "This is an AI-enriched attribute. Enrichment is performed using fine-tuned LLMs that reference various open sources to determine revenue of the company in the last quarter. Note: In some cases, data may not be available at the source level.",
  [WorkspaceColumnKey.CompanyRevenueRange]: "This is an AI-enriched attribute. Enrichment is performed using fine-tuned LLMs that reference various open sources to determine revenue of the company in the last quarter. Note: In some cases, data may not be available at the source level.",
  [WorkspaceColumnKey.CompanyDomain]: "This is an AI-enriched attribute. Enrichment is performed using fine-tuned LLMs that reference various open sources to determine company's website. Note: Some companies won't have their website.",
};

export const avatarPalette = ["#fce7f3", "#fef3c7", "#dcfce7", "#dbeafe", "#ede9fe", "#fee2e2"];

export const getAvatarBg = (name: string): string => {
  const charCode = (name || "A").toUpperCase().charCodeAt(0);
  return avatarPalette[charCode % avatarPalette.length];
};

export const normalizeTradingPartnersPayload = (responseData: Record<string, unknown>): { partners: TradingPartner[]; totalCount: number } => {
  const tp = (responseData?.trading_partners ?? {}) as Record<string, unknown>;
  const partners: TradingPartner[] = Array.isArray(tp.top) ? tp.top : [];
  const totalCount = (tp.count ?? 0) as number;
  return { partners, totalCount };
};

export const extractColumnsPayload = (responseData: Record<string, unknown>): WorkspaceColumn[] => {
  const rawColumns = (responseData?.columns || []) as WorkspaceColumn[];
  if (!Array.isArray(rawColumns)) return [];
  return [...rawColumns].sort((a, b) => (a?.display_order ?? Number.MAX_SAFE_INTEGER) - (b?.display_order ?? Number.MAX_SAFE_INTEGER));
};

export const mapWorkspaceRowToEnrichmentCompany = (row: WorkspaceRow): EnrichmentCompany => ({
  company_name: String(row?.company_name ?? ""),
  company_country: String(row?.country ?? ""),
  address: String(row?.full_address ?? ""),
  company_id: String(row?.company_id ?? ""),
  company_type: String(row?.company_type ?? ""),
});

export const formatAiSummaryHtml = (value: unknown): string => DOMPurify.sanitize(String(value || ""), { USE_PROFILES: { html: true } }).replace(/<ul>/g, '<ul class="tw-flex tw-flex-col tw-gap-3">');

export const getWorkspaceRowKey = (row: WorkspaceRow): string => String(row?.company_id ?? "");

export const INFO_ICON_COLUMNS = new Set<string>([WorkspaceColumnKey.Shipments, WorkspaceColumnKey.Weight, WorkspaceColumnKey.ShipmentValue, WorkspaceColumnKey.FirstImportedOn, WorkspaceColumnKey.LastImportedOn, WorkspaceColumnKey.TradingPartners, WorkspaceColumnKey.TradingCountries]);

export const RIGHT_ALIGN_LABELS = new Set<string>([WorkspaceColumnKey.Shipments, WorkspaceColumnKey.Weight, WorkspaceColumnKey.ShipmentValue]);

export const DATE_SORT_LABELS = new Set<string>([WorkspaceColumnKey.FirstImportedOn, WorkspaceColumnKey.LastImportedOn]);

export const COLUMN_WIDTHS: Partial<Record<WorkspaceColumnKey, number>> = {
  [WorkspaceColumnKey.CompanyName]: 280,
  [WorkspaceColumnKey.CompanyDescription]: 300,
  [WorkspaceColumnKey.CompanyIndustry]: 300,
  [WorkspaceColumnKey.PricingAgent]: 300,
  [WorkspaceColumnKey.SanctionsScreening]: 220,
  [WorkspaceColumnKey.TraderManufacturer]: 200,
  [WorkspaceColumnKey.AiSummary]: 300,
  [WorkspaceColumnKey.CompanyDomain]: 300,
  [WorkspaceColumnKey.ShipmentValue]: 250,
  [WorkspaceColumnKey.CompanyCompetitors]: 150,
};

export const getTradingPartnersCellKey = (row: WorkspaceRow): string | null => {
  const companyId = row.company_id;
  const companyType = row.company_type;
  if (!companyId) return null;
  return `${companyId}-${companyType}`;
};
